import React, { useImperativeHandle, useState, forwardRef } from "react";
import {
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
  TextInput,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Toast from "react-native-simple-toast";

import CloseIcon from "@assets/svg/CloseIcon";
import Button from "@components/Button";
import Text from "@components/CustomText";
import RadioButton from "@components/RadioButton";
import colors from "@constants/color";
import { normalize } from "@utils/metrics_1";
import t from "@constants/localization";
import PressableButton from "@components/PrimaryButton";
import { deleteClUser } from "@services/api";
import analytics from "@services/analytics";
import { CUSTOMER_PAGE } from "@constants/analyticsEventsCT";
import { useSelector } from "react-redux";

const PATH = "components/DeleteCXModal";

const DeleteCXModal = forwardRef(({ user, navigation }, ref) => {
  const reasons = useSelector(
    (state) => state.delayedDataReducer?.cpDeleteCxReasons?.reasons || [],
  );
  const insets = useSafeAreaInsets();

  const [selected, setSelected] = useState();
  const [text, setText] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const handleShowModal = () => {
    setShowConfirmationModal(true);
  };

  const handleHideModal = () => {
    setSelected();
    setShowConfirmationModal(false);
  };

  useImperativeHandle(ref, () => ({
    showModal() {
      handleShowModal();
    },
    hideModal() {
      handleHideModal();
    },
  }));

  const handleSubmit = () => {
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    deleteClUser(user.user_id, selected)
      .then(({ data }) => {
        setShowConfirmationModal(false);
        setIsDeleteModalVisible(false);
        if (data?.should_show_info_alert) {
          Alert.alert(
            data?.title,
            data?.message,
            [
              {
                text: t(`${PATH}/okay`),
                onPress: () => {
                  navigation && navigation.goBack();
                },
              },
            ],
            { cancelable: false },
          );
          return;
        }
        navigation && navigation.goBack();
        Toast.show(t(`${PATH}/successMsg`), Toast.LONG, Toast.BOTTOM);
      })
      .catch((error) => {
        console.log(error);
      });
    analytics.trackEvent(CUSTOMER_PAGE.CX_DETAIL_DELETE, {
      cx_id: user?.user_id,
    });
  };

  const isSubmitBtnDisabled =
    selected !== undefined
      ? selected === "OTHERS"
        ? text.length <= 10
        : false
      : true;

  return (
    <>
      <Modal
        transparent
        visible={showConfirmationModal}
        onRequestClose={handleHideModal}
        animationType="fade"
        statusBarTranslucent
        navigationBarTranslucent
      >
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
        >
          <View
            style={{
              flex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            }}
          >
            <View
              style={[styles.modalContainer, { paddingBottom: insets.bottom }]}
            >
              <View style={styles.modalHeader}>
                <Text
                  variant="entityHeading"
                  style={{
                    lineHeight: 24,
                    fontSize: 16,
                  }}
                >
                  {t(`${PATH}/title`)}
                </Text>
                <TouchableOpacity
                  testID="dcxm-close-modal"
                  onPress={() => {
                    handleHideModal();
                  }}
                >
                  <CloseIcon fillOpacity={1} width={24} height={24} />
                </TouchableOpacity>
              </View>
              <ScrollView>
                <RadioButton
                  radioOptions={reasons}
                  setSelectedOption={(key) => {
                    setSelected(key);
                  }}
                  hindiText
                  labelColor="rgba(0, 0, 0, 0.87)"
                  radioColor={colors.primary}
                  buttonContainerBorder={{
                    borderBottomColor: colors.white,
                  }}
                />
                <View
                  style={{
                    paddingHorizontal: 10,
                  }}
                >
                  {selected === "OTHER_REASON" && (
                    <TextInput
                      testID="dcxm-other-reason"
                      placeholder={t(`${PATH}/typeHere`)}
                      multiline={true}
                      numberOfLines={2}
                      onChangeText={(newText) => setText(newText)}
                      style={styles.textInput}
                      maxLength={100}
                      autoFocus={true}
                    />
                  )}
                </View>
              </ScrollView>
              <View style={{ marginTop: 10 }}>
                <Button
                  borderRadius={10}
                  btnText={t(`${PATH}/submit`)}
                  buttonWidth="95%"
                  containerStyle={styles.btnContainer}
                  height={56}
                  shouldBoldText
                  buttonFontType="head3"
                  disabled={isSubmitBtnDisabled}
                  onPress={() => handleSubmit()}
                />
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
      <ConfirmationModal
        visible={isDeleteModalVisible}
        setVisible={setIsDeleteModalVisible}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
});

const ConfirmationModal = ({ visible, setVisible, onConfirm }) => {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={() => setVisible(false)}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <View style={styles.modalWrapper}>
        <View style={styles.container}>
          <Text style={styles.title}>{t(`${PATH}/cnfmMsg`)}</Text>
          <Text style={styles.description}>{t(`${PATH}/desc`)}</Text>
          <View style={styles.buttonContainer}>
            <PressableButton
              text={t(`${PATH}/no`)}
              buttonStyle={styles.button}
              textStyle={styles.cancelBtnText}
              buttonColor={colors.white}
              onPress={() => setVisible(false)}
            />
            <PressableButton
              text={t(`${PATH}/yes`)}
              buttonStyle={styles.button}
              textStyle={styles.cnfmBtnText}
              buttonColor={colors.white}
              onPress={() => onConfirm()}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  modalContainer: {
    backgroundColor: "#fff",
    position: "absolute",
    bottom: 0,
    width: "100%",
    paddingTop: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: "98%",
  },
  modalHeader: {
    justifyContent: "space-between",
    flexDirection: "row",
    marginHorizontal: 12,
  },
  textInput: {
    borderWidth: 1,
    fontSize: normalize(14),
    lineHeight: normalize(20),
    borderRadius: 4,
    borderColor: colors.extraLightBlack,
    width: "95%",
    alignSelf: "center",
  },
  btnContainer: {
    alignSelf: "center",
    marginBottom: 10,
  },
  modalWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.lightBlack,
  },
  container: {
    backgroundColor: colors.white,
    padding: 24,
    width: "92.5%",
    borderRadius: 6,
  },
  title: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 20,
    fontWeight: "700",
    lineHeight: 30,
    textAlign: "left",
  },
  description: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
    textAlign: "left",
    marginVertical: 16,
    color: colors.lightBlack,
    width: "85%",
  },
  buttonContainer: {
    alignItems: "center",
    marginTop: 10,
    width: "100%",
    paddingHorizontal: 10,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  button: {
    paddingVertical: 6,
    marginHorizontal: 4,
    minWidth: "25%",
    paddingHorizontal: 0,
    borderRadius: 6,
    alignSelf: "flex-end",
  },
  cnfmBtnText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    color: colors.primary,
  },
  cancelBtnText: {
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.black,
    fontSize: 16,
  },
});

export default DeleteCXModal;
